// useTranslated — TanStack Query hook that fetches localized field text from
// /v1/translate/entity and falls back to the source value on any failure.
//
// Contract:
//   - When `targetLang` matches the entity's source language (today: 'en'
//     for federal procurement notices) the hook returns null without
//     fetching, letting the caller render the source value directly.
//   - Cache key is (entity_type, entity_id, sorted fields, target_lang).
//     Switching target_lang refetches because the lang participates in the
//     key; switching back hits cache.
//   - On error (network, 5xx, 503 inference_unavailable, etc.) the hook
//     returns null and the caller falls back to source. UI never breaks.
//
// Wired into the brief / opportunity detail surface only today (the most-
// translated entity in the product). Other consumers stay on the source
// values until the platform-level locale picker lands.

import { apiFetch, isInferenceUnavailableError } from '@/lib/api';
import type { translation as tr } from '@eidollon/contracts';
import { useQuery } from '@tanstack/react-query';

export type TranslatedFieldMap = Record<string, tr.EntityTranslateFieldResult>;

export interface UseTranslatedResult {
  /** Map of field_name → { text, language, quality }. Empty object when
   *  loading or on error — never null so the caller can safely destructure. */
  fields: TranslatedFieldMap;
  isLoading: boolean;
  /** True when the call failed (network, 5xx, inference offline). UI should
   *  silently fall back to source values. */
  failed: boolean;
}

// Translations are durably cached server-side in the `translations` table;
// the client-side cache only needs to keep render-time roundtrips from
// re-issuing the same fetch on every mount. 24h respects Iron Rule #12's
// per-tenant LLM budget cap: a row that was translated this morning won't
// re-bill tonight.
const STALE_MS = 24 * 60 * 60 * 1_000;
const SOURCE_LANG = 'en';

export function useTranslated(
  entity: tr.TranslatableEntityType,
  entityId: string | undefined,
  fields: readonly string[],
  targetLang: tr.SupportedTargetLang,
): UseTranslatedResult {
  const enabled = !!entityId && fields.length > 0 && targetLang !== SOURCE_LANG;

  // Sort fields so the key is stable regardless of caller ordering.
  const sortedFields = [...fields].sort();
  const queryKey = ['translate.entity', entity, entityId, sortedFields.join(','), targetLang];

  const query = useQuery<TranslatedFieldMap>({
    queryKey,
    enabled,
    staleTime: STALE_MS,
    // Don't retry the inference-unavailable case — degrade to source text
    // immediately. Network blips still get one retry (TanStack default).
    retry: (failureCount, err) => {
      if (isInferenceUnavailableError(err)) return false;
      return failureCount < 1;
    },
    queryFn: async () => {
      const body: tr.EntityTranslateRequest = {
        entity_type: entity,
        entity_id: entityId!,
        fields: [...sortedFields],
        target_lang: targetLang,
      };
      const res = await apiFetch<tr.EntityTranslateResponse>('/v1/translate/entity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.fields ?? {};
    },
  });

  return {
    fields: query.data ?? {},
    isLoading: enabled && query.isLoading,
    failed: !!query.error,
  };
}
